import type { AccountAnalyticsResponse } from "@/shared/api";

export type AccountAnalyticsEvidence = {
  readonly realisedNet: string;
  readonly cycles: number;
  readonly winners: number;
  readonly losers: number;
  readonly portfolioPnl: string | null;
  readonly ownerReturn: string | null;
  readonly maximumDrawdown: string | null;
  readonly fees: string;
  readonly funding: string;
  readonly portfolioAvailable: boolean;
  readonly status: "current" | "partial" | "stale";
  readonly asOf: string;
  readonly ageSeconds: number | null;
  readonly coverageSince: string | null;
};

function hasRecordedEvidence(analytics: AccountAnalyticsResponse): boolean {
  return (
    analytics.summary.cycles !== 0 ||
    analytics.portfolio.length !== 0 ||
    analytics.realised.length !== 0 ||
    analytics.pools.length !== 0
  );
}

/** Shared truth projection for compact and detailed Analytics presentation. */
export function projectAccountAnalytics(
  analytics: AccountAnalyticsResponse,
): AccountAnalyticsEvidence | null {
  if (analytics.effective_since === null && hasRecordedEvidence(analytics)) return null;

  const portfolioAvailable = analytics.covered && !analytics.stale;
  // The deposit rate is not one of the equity-curve figures. It divides closed
  // cycles by the capital the owner put in, and both are known from the first
  // day — so withholding it when the equity record is shorter than the period
  // threw away a figure the server had measured. That is why seven days showed
  // a dash on a screen whose server had answered 15.9 %.
  const rateWithheld = analytics.stale;
  return {
    realisedNet: analytics.summary.realised_net,
    cycles: analytics.summary.cycles,
    winners: analytics.summary.winners,
    losers: analytics.summary.losers,
    portfolioPnl: portfolioAvailable ? analytics.summary.portfolio_pnl : null,
    ownerReturn: rateWithheld ? null : analytics.summary.owner_return,
    maximumDrawdown: portfolioAvailable ? analytics.summary.maximum_drawdown : null,
    fees: analytics.summary.fees,
    funding: analytics.summary.funding,
    portfolioAvailable,
    status: analytics.stale ? "stale" : analytics.covered ? "current" : "partial",
    asOf: analytics.as_of,
    ageSeconds: analytics.age_seconds,
    coverageSince: analytics.coverage_since,
  };
}
